import { useCallback, useState } from 'react'
import {
  createActivityExploreComment,
  createInstagramReelComment,
  createPodcastComment,
  createSymptomComment,
  createWebinarSuggestionComment,
  createWebinarComment,
  createReply,
  deleteComment as deleteCommentAction,
  deleteReply as deleteReplyAction,
  toggleLikeComment as toggleLikeCommentAction,
  toggleLikeReply as toggleLikeReplyAction,
  updateComment as updateCommentAction,
  updateReply as updateReplyAction,
} from '@domain/features/activity-comments/activity-comments-feature'
import type {
  ActivityComment,
  CommentsOnType,
  CommentReply,
} from '@domain/models/activity-comments'
import { CommentsOn } from '@domain/models/activity-comments'
import { useAuthUser } from '@presentation/hooks/useAuthUser'

export function useActivityCommentsActions(
  targetId: string | undefined,
  commentsOn: CommentsOnType = CommentsOn.ActivityExplore,
  symptomId?: string,
) {
  const { isAuthenticated } = useAuthUser()
  const [mutating, setMutating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const runAuthAction = useCallback(
    async (
      action: () => Promise<void>,
      onAuthRequired?: () => void,
    ): Promise<boolean> => {
      if (!isAuthenticated) {
        onAuthRequired?.()
        return false
      }

      try {
        setError(null)
        setMutating(true)
        await action()
        return true
      } catch (nextError) {
        setError(
          nextError instanceof Error
            ? nextError.message
            : 'Failed to process comment action.',
        )
        return false
      } finally {
        setMutating(false)
      }
    },
    [isAuthenticated],
  )

  const createComment = useCallback(
    async (comment: string, onAuthRequired?: () => void): Promise<string | null> => {
      if (!targetId) {
        return null
      }

      if (!isAuthenticated) {
        onAuthRequired?.()
        return null
      }

      const createCommentAction = commentsOn === CommentsOn.Webinar
        ? (nextTargetId: string, nextComment: string) =>
            createWebinarComment(nextTargetId, nextComment)
        : commentsOn === CommentsOn.Podcast
          ? (nextTargetId: string, nextComment: string) =>
              createPodcastComment(nextTargetId, nextComment)
        : commentsOn === CommentsOn.InstagramReel
          ? (nextTargetId: string, nextComment: string) =>
              createInstagramReelComment(nextTargetId, nextComment)
          : commentsOn === CommentsOn.WebinarSuggestion
            ? (nextTargetId: string, nextComment: string) =>
                createWebinarSuggestionComment(nextTargetId, nextComment)
        : commentsOn === CommentsOn.Symptom
          ? (nextTargetId: string, nextComment: string) => {
              if (!symptomId) {
                throw new Error('Symptom id is required for symptom comments.')
              }
              return createSymptomComment(symptomId, nextTargetId, nextComment)
            }
          : (nextTargetId: string, nextComment: string) =>
              createActivityExploreComment(nextTargetId, nextComment)

      try {
        setError(null)
        setMutating(true)
        return await createCommentAction(targetId, comment)
      } catch (nextError) {
        setError(
          nextError instanceof Error
            ? nextError.message
            : 'Failed to process comment action.',
        )
        return null
      } finally {
        setMutating(false)
      }
    },
    [commentsOn, isAuthenticated, symptomId, targetId],
  )

  const toggleCommentLike = useCallback(
    async (
      comment: ActivityComment,
      onAuthRequired?: () => void,
    ): Promise<boolean> => runAuthAction(
      () => toggleLikeCommentAction(comment).then(() => undefined),
      onAuthRequired,
    ),
    [runAuthAction],
  )

  const updateComment = useCallback(
    async (
      commentId: string,
      comment: string,
      onAuthRequired?: () => void,
    ): Promise<boolean> =>
      runAuthAction(() => updateCommentAction(commentId, comment), onAuthRequired),
    [runAuthAction],
  )

  const deleteComment = useCallback(
    async (commentId: string, onAuthRequired?: () => void): Promise<boolean> =>
      runAuthAction(() => deleteCommentAction(commentId), onAuthRequired),
    [runAuthAction],
  )

  const createCommentReply = useCallback(
    async (
      commentId: string,
      replyText: string,
      onAuthRequired?: () => void,
    ): Promise<boolean> =>
      runAuthAction(
        () => createReply(commentId, replyText).then(() => undefined),
        onAuthRequired,
      ),
    [runAuthAction],
  )

  const toggleReplyLike = useCallback(
    async (
      commentId: string,
      reply: CommentReply,
      onAuthRequired?: () => void,
    ): Promise<boolean> => runAuthAction(
      () => toggleLikeReplyAction(commentId, reply).then(() => undefined),
      onAuthRequired,
    ),
    [runAuthAction],
  )

  const updateCommentReply = useCallback(
    async (
      commentId: string,
      replyId: string,
      replyText: string,
      onAuthRequired?: () => void,
    ): Promise<boolean> =>
      runAuthAction(
        () => updateReplyAction(commentId, replyId, replyText),
        onAuthRequired,
      ),
    [runAuthAction],
  )

  const deleteCommentReply = useCallback(
    async (
      commentId: string,
      replyId: string,
      onAuthRequired?: () => void,
    ): Promise<boolean> =>
      runAuthAction(() => deleteReplyAction(commentId, replyId), onAuthRequired),
    [runAuthAction],
  )

  return {
    mutating,
    error,
    createComment,
    toggleCommentLike,
    updateComment,
    deleteComment,
    createCommentReply,
    toggleReplyLike,
    updateCommentReply,
    deleteCommentReply,
  }
}
